import { ArrowBack, CheckCircle, Email } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Link as MuiLink,
  TextField,
  Typography,
} from '@mui/material';
import { sendPasswordResetEmail } from 'firebase/auth';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../contexts/ToastContext';
import { auth } from '../firebase';
import { colors } from '../theme';

const ForgotPasswordPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // URL'den userRole'ü al (örn: /dietitian/forgot-password veya /client/forgot-password)
  const userRole = location.pathname.includes('/dietitian/') ? 'dietitian' : 'client';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!email) {
      setError('Lütfen e-posta adresinizi giriniz.');
      setLoading(false);
      return;
    }

    // Email format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Lütfen geçerli bir e-posta adresi giriniz.');
      setLoading(false);
      return;
    }

    try {
      // Önce backend'de kullanıcının kayıtlı olup olmadığını kontrol et
      const response = await fetch(`${API_ENDPOINTS.auth}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          userRole: userRole || 'client',
          resetPasswordUrl: `${window.location.origin}/${userRole || 'client'}/reset-password`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kullanıcı kontrolü yapılamadı.');
      }

      // Backend kontrolü başarılı, şimdi Firebase'den şifre sıfırlama email'i gönder
      // Firebase'in action code settings'i
      const actionCodeSettings = {
        url: `${window.location.origin}/${userRole || 'client'}/reset-password`,
        handleCodeInApp: false,
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);

      setSuccess(true);
      showSuccess(
        'Şifre sıfırlama linki e-posta adresinize gönderildi. Lütfen e-postanızı kontrol edin.',
        'E-posta Gönderildi'
      );
    } catch (error: any) {
      console.error('Şifre sıfırlama hatası:', error);
      
      let errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Geçersiz e-posta adresi.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Çok fazla istek gönderildi. Lütfen daha sonra tekrar deneyin.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      showError(errorMessage, 'Hata');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #66BB6A 0%, #43A047 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      {/* Geri Butonu */}
      <IconButton
        onClick={() => navigate(`/${userRole}/login`)}
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          width: '48px',
          height: '48px',
          zIndex: 1000,
          bgcolor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(30px)',
          color: 'rgba(0, 0, 0, 0.6)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            color: 'rgba(0, 0, 0, 0.8)',
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <ArrowBack />
      </IconButton>

      <Container maxWidth="sm">
        <Card elevation={6}>
          <CardContent sx={{ p: 4 }}>
            {/* Başlık */}
            <Box textAlign="center" mb={4}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Şifremi Unuttum
              </Typography>
              <Typography variant="body2" color="text.secondary">
                E-posta adresinizi girin, size şifre sıfırlama linki gönderelim
              </Typography>
            </Box>

            {/* Success Message */}
            {success && (
              <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{ mb: 3, animation: 'fadeIn 0.3s ease-in' }}
              >
                <Typography fontWeight={600}>
                  E-posta gönderildi!
                </Typography>
                <Typography variant="body2">
                  {email} adresine şifre sıfırlama linki gönderildi. Lütfen
                  e-postanızı kontrol edin ve spam klasörünü de kontrol etmeyi
                  unutmayın.
                </Typography>
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Form */}
            {!success && (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="E-posta Adresi"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  autoFocus
                  placeholder="ornek@email.com"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <Email sx={{ color: colors.primary.main }} />
                      </Box>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: colors.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary.main,
                        borderWidth: 2,
                      },
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: colors.gradients.primary,
                    '&:hover': {
                      background: colors.gradients.primaryMedium,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? 'Gönderiliyor...' : 'Şifre Sıfırlama Linki Gönder'}
                </Button>
              </form>
            )}

            {/* Alt Bilgi */}
            <Box textAlign="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                Şifrenizi hatırladınız mı?{' '}
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate(`/${userRole}/login`)}
                  disabled={loading}
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.6 : 1,
                    '&:hover': {
                      color: colors.primary.dark,
                      backgroundColor: 'rgba(67, 160, 71, 0.08)',
                      textDecoration: 'none',
                    },
                    '&:active': {
                      backgroundColor: 'rgba(67, 160, 71, 0.12)',
                    },
                  }}
                >
                  Giriş Yap
                </MuiLink>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;

